import {
  type JSX,
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import {
  AssistantLayout,
  type AssistantPanelState,
} from '@/features/assistant/AssistantLayout.js';
import { consumeAssistantSuggestionEscape } from '@/features/assistant/assistantSuggestionEscape.js';
import { cn } from '@/lib/utils.js';

import {
  FULL_SCREEN_TOGGLE_SELECTOR,
  MAXIMIZED_DIALOG_CLASS,
} from './FullScreenToggle.js';
import {
  leaveFullScreenOnEscape,
  useFullScreenShortcut,
} from './useFullScreenShortcut.js';

/** The dialog's width while the Assistant is open beside the preview: room for both. */
const ASSISTANT_WIDTH_CLASS = 'max-w-7xl';

/** A viewer's full-screen state: whether it is on, and the two ways to change it. */
export interface ViewerFullScreen {
  maximized: boolean;
  toggle: () => void;
  leave: () => void;
}

/**
 * Full screen for a file viewer: off at first, toggled by the button or a bare F, and only ever on while
 * `canMaximize` — a viewer with nothing to enlarge, or with its editor open, never shows full screen.
 */
export function useViewerFullScreen(canMaximize: boolean): ViewerFullScreen {
  const [chosen, setChosen] = useState(false);
  const toggle = useCallback(() => setChosen((value) => !value), []);
  const leave = useCallback(() => setChosen(false), []);
  useFullScreenShortcut(canMaximize, toggle);
  return { maximized: chosen && canMaximize, toggle, leave };
}

/**
 * Moving the toolbar between its own row and the title line mounts it afresh, which drops the focus that a
 * press on the full-screen toggle left on it. When a switch leaves focus nowhere, it goes back to the toggle.
 */
function useToolbarFocusAcrossSwitch(
  maximized: boolean,
): RefObject<HTMLDivElement | null> {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const shownMaximized = useRef(maximized);
  useLayoutEffect(() => {
    if (shownMaximized.current === maximized) return;
    shownMaximized.current = maximized;
    if (document.activeElement !== document.body) return;
    toolbarRef.current
      ?.querySelector<HTMLElement>(FULL_SCREEN_TOGGLE_SELECTOR)
      ?.focus();
  }, [maximized]);
  return toolbarRef;
}

/**
 * The frame every file viewer shares — a workspace artifact, or a file from SharePoint or OneDrive — so they
 * look and behave the same: one title line with the file's size after its name, a toolbar of badges, buttons
 * and the actions menu, the preview, and room for a banner above the preview and a band below it.
 *
 * In full screen the toolbar leaves its own row and docks at the right end of the title line, left of the
 * close button, sliding up into place; the dialog then fills the window, so the preview gets every pixel
 * the chrome gave back. On a narrow screen the docked toolbar wraps onto a second line rather than run
 * under the close button. Escape leaves full screen first and closes the viewer on the next press.
 *
 * Focus stays on the full-screen toggle across that move, so a keyboard user does not lose their place.
 *
 * Layout: the dialog is a bounded flex column that CLIPS (`overflow-hidden` beats the shared dialog's own
 * `overflow-y-auto` through `cn`), and only the preview scrolls. With `fill`, the dialog takes a DEFINITE
 * height and the preview fills it, because a percentage height needs a definite one to resolve against; a
 * self-sized preview inside a shorter box showed two scrollbars for one file. A content-sized body — the
 * inline editor, or a slide that takes its own shape — passes `fill={false}` and gives the height back.
 */
export function FileViewerShell({
  title,
  titleMeta,
  widthClass = 'max-w-3xl',
  fill = true,
  maximized,
  onLeaveFullScreen,
  onClose,
  toolbar,
  banner,
  footer,
  assistant,
  helpHref,
  children,
}: {
  title: string;
  /** The file's size, or nothing when the source does not say. */
  titleMeta?: string;
  /** The dialog's width outside full screen: portrait for a page, wide for a deck or a workbook. */
  widthClass?: string;
  fill?: boolean;
  maximized: boolean;
  onLeaveFullScreen: () => void;
  onClose: () => void;
  toolbar: ReactNode;
  /** Shown above the preview, such as the note that an older version is on screen. */
  banner?: ReactNode;
  /** Shown below the preview, such as the version history. */
  footer?: ReactNode;
  /** The Assistant beside the preview and its band, in the normal viewer and in full screen alike. */
  assistant?: {
    panel: AssistantPanelState;
    chat: (collapse: () => void) => ReactNode;
  };
  /** The guide entry behind the question mark beside the close button. */
  helpHref?: string;
  children: ReactNode;
}): JSX.Element {
  const toolbarRef = useToolbarFocusAcrossSwitch(maximized);
  const assistantRef = useRef<HTMLDivElement | null>(null);
  const assistantOpen = assistant !== undefined && !assistant.panel.collapsed;
  const toolbarRow = (
    <div
      ref={toolbarRef}
      data-testid="artifact-viewer-topbar"
      data-docked={maximized ? 'true' : 'false'}
      className={cn(
        'flex items-center gap-2',
        maximized
          ? 'flex-wrap justify-end motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-base'
          : 'shrink-0 flex-wrap',
      )}
    >
      {toolbar}
    </div>
  );
  const preview = (
    <>
      <div
        data-testid="artifact-viewer-content"
        className={cn(
          'min-h-0 flex-1',
          fill ? 'overflow-hidden' : 'overflow-y-auto overscroll-contain',
        )}
      >
        <div
          data-testid="artifact-viewer-preview"
          className={fill ? 'h-full' : 'min-h-40'}
        >
          {children}
        </div>
      </div>
      {footer}
    </>
  );
  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      titleMeta={titleMeta ?? ''}
      titleAction={maximized ? toolbarRow : undefined}
      className={cn(
        'flex flex-col overflow-hidden',
        maximized
          ? cn(MAXIMIZED_DIALOG_CLASS, 'gap-3 p-4')
          : assistantOpen
            ? ASSISTANT_WIDTH_CLASS
            : widthClass,
        fill && !maximized && 'h-[calc(var(--app-height,100dvh)-2rem)]',
      )}
      onEscapeKeyDown={(event) => {
        if (consumeAssistantSuggestionEscape(event)) return;
        leaveFullScreenOnEscape(maximized, onLeaveFullScreen)(event);
      }}
      helpHref={helpHref}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {!maximized && toolbarRow}
        {banner}
        {assistant ? (
          <AssistantLayout
            containerRef={assistantRef}
            panel={assistant.panel}
            chat={assistant.chat}
            fitParent
            main={preview}
          />
        ) : (
          preview
        )}
      </div>
    </AppDialog>
  );
}
